import { useMemo, useState } from 'react';

import { PlaybackButton, usePlaybackControl, usePlaybackTransport } from '../../player/index.js';
import type { PlaybackItem } from '../../player/index.js';
import { StatusPill } from '../../design-system/index.js';
import { PageHeader, SegmentedControl } from '../shared/page.js';
import { KK7_BOUNDARY_AUDIT_TRACKS } from './kk7-boundary-audit-data.js';
import {
  KK7_BOUNDARY_AUDIT_PROJECT_ID,
  filterKk7BoundaryAuditTracks,
  violatesKk7BoundaryRule,
  type Kk7BoundaryAuditCohortFilter,
  type Kk7BoundaryAuditFilter,
  type Kk7BoundaryAuditTrack,
} from './kk7-boundary-audit-model.js';

const AUDIT_PATH = '/audit/onset/kk7';

function playbackItemFromAuditTrack(track: Kk7BoundaryAuditTrack): PlaybackItem {
  return {
    id: track.id,
    title: track.title,
    artist: null,
    album: 'KK7 onset check',
    detailHref: AUDIT_PATH,
    composerProjectId: KK7_BOUNDARY_AUDIT_PROJECT_ID,
    sourcePreference: 'direct',
    directUrl: track.audioUrl,
  };
}

function DetectionResult({ track }: { track: Kk7BoundaryAuditTrack }) {
  const violates = violatesKk7BoundaryRule(track);
  return (
    <StatusPill
      className="shrink-0 whitespace-nowrap"
      status={violates ? 'violates' : 'does_not_violate'}
      tone={violates ? 'danger' : 'neutral'}
    >
      {violates ? 'Violates' : 'Does not violate'}
    </StatusPill>
  );
}

function AuditPlaybackButton({
  item,
  onStart,
}: {
  item: PlaybackItem;
  onStart: () => Promise<void>;
}) {
  const control = usePlaybackControl(item, onStart);
  return (
    <PlaybackButton
      className="size-11 rounded-md border-border bg-[linear-gradient(135deg,var(--surface-hi),var(--bg-2))] text-ink-2 hover:border-accent-line hover:text-ink focus-visible:outline-accent [&_svg]:size-5"
      control={control}
      size="icon"
      unavailableLabel={`Playback unavailable for ${item.title}`}
      variant="ghost"
    />
  );
}

function AuditTrackRow({
  item,
  onStart,
  track,
}: {
  item: PlaybackItem;
  onStart: () => Promise<void>;
  track: Kk7BoundaryAuditTrack;
}) {
  const removalLabel = track.cohort === 'removed' ? 'Removed' : 'Not removed';
  return (
    <>
      <article className="hidden min-h-[4.75rem] grid-cols-[2.75rem_3.25rem_minmax(12rem,1fr)_8rem_10rem] items-center gap-3 border-b border-border px-4 py-3 last:border-b-0 md:grid">
        <AuditPlaybackButton item={item} onStart={onStart} />
        <span className="font-mono text-xs font-semibold tabular-nums text-ink-3">
          #{track.rank}
        </span>
        <div className="min-w-0 truncate text-sm font-semibold text-ink">{track.title}</div>
        <span className="font-mono text-xs text-ink-3">{removalLabel}</span>
        <div className="flex justify-end">
          <DetectionResult track={track} />
        </div>
      </article>

      <article className="rounded-md border border-border bg-surface p-3 md:hidden">
        <div className="flex items-center gap-3">
          <AuditPlaybackButton item={item} onStart={onStart} />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="shrink-0 font-mono text-xs font-semibold tabular-nums text-ink-3">
                #{track.rank}
              </span>
              <span className="truncate text-sm font-semibold text-ink">{track.title}</span>
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3 border-t border-border pt-2">
          <p className="font-mono text-xs text-ink-3">{removalLabel}</p>
          <DetectionResult track={track} />
        </div>
      </article>
    </>
  );
}

export function Kk7BoundaryAuditPage() {
  const player = usePlaybackTransport();
  const [filter, setFilter] = useState<Kk7BoundaryAuditFilter>('all');
  const [cohort, setCohort] = useState<Kk7BoundaryAuditCohortFilter>('all');
  const [query, setQuery] = useState('');

  const violatesCount = KK7_BOUNDARY_AUDIT_TRACKS.filter(violatesKk7BoundaryRule).length;
  const groupCounts = {
    removedViolates: KK7_BOUNDARY_AUDIT_TRACKS.filter(
      (track) => track.cohort === 'removed' && violatesKk7BoundaryRule(track),
    ).length,
    removedPasses: KK7_BOUNDARY_AUDIT_TRACKS.filter(
      (track) => track.cohort === 'removed' && !violatesKk7BoundaryRule(track),
    ).length,
    retainedViolates: KK7_BOUNDARY_AUDIT_TRACKS.filter(
      (track) => track.cohort === 'retained' && violatesKk7BoundaryRule(track),
    ).length,
    retainedPasses: KK7_BOUNDARY_AUDIT_TRACKS.filter(
      (track) => track.cohort === 'retained' && !violatesKk7BoundaryRule(track),
    ).length,
  };
  const visibleTracks = useMemo(
    () => filterKk7BoundaryAuditTracks(KK7_BOUNDARY_AUDIT_TRACKS, { filter, cohort, query }),
    [cohort, filter, query],
  );
  const playbackItems = useMemo(
    () => visibleTracks.map(playbackItemFromAuditTrack),
    [visibleTracks],
  );
  const itemById = useMemo(
    () => new Map(playbackItems.map((item) => [item.id, item])),
    [playbackItems],
  );
  const playbackContext = useMemo(
    () => ({
      kind: 'project' as const,
      id: `kk7-onset-check:${filter}:${cohort}:${query}`,
      label: `KK7 onset check · ${visibleTracks.length} tracks`,
    }),
    [cohort, filter, query, visibleTracks.length],
  );
  const startPlayback = async (trackId: string) => {
    const startIndex = playbackItems.findIndex((item) => item.id === trackId);
    if (startIndex >= 0) await player.playQueue(playbackItems, startIndex, playbackContext);
  };

  const detectionOptions = [
    { label: `All ${KK7_BOUNDARY_AUDIT_TRACKS.length}`, value: 'all' },
    { label: `Violates ${violatesCount}`, value: 'violates' },
    {
      label: `Does not violate ${KK7_BOUNDARY_AUDIT_TRACKS.length - violatesCount}`,
      value: 'does-not-violate',
    },
  ] as const;

  const groups = [
    {
      label: 'Removed · Violates',
      value: groupCounts.removedViolates,
      tone: 'text-err',
      cohort: 'removed',
      filter: 'violates',
    },
    {
      label: 'Removed · Does not violate',
      value: groupCounts.removedPasses,
      tone: 'text-ink',
      cohort: 'removed',
      filter: 'does-not-violate',
    },
    {
      label: 'Not removed · Violates',
      value: groupCounts.retainedViolates,
      tone: 'text-err',
      cohort: 'retained',
      filter: 'violates',
    },
    {
      label: 'Not removed · Does not violate',
      value: groupCounts.retainedPasses,
      tone: 'text-ink',
      cohort: 'retained',
      filter: 'does-not-violate',
    },
  ] as const;

  return (
    <div>
      <PageHeader
        eyebrow="Direct check · KK7 · all 362 outputs"
        title="Opening-boom check"
        description="Removed / Not removed is your catalog history. Violates / Does not violate is the current script result. Nothing on this page changes or deletes songs."
      />

      <section
        className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4"
        aria-label="KK7 result groups"
      >
        {groups.map((group) => (
          <button
            key={group.label}
            type="button"
            onClick={() => {
              setCohort(group.cohort);
              setFilter(group.filter);
            }}
            className="min-h-20 rounded-md border border-border bg-surface px-4 py-3 text-left hover:border-accent-line focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <span className="block font-mono text-xs uppercase tracking-[0.08em] text-ink-3">
              {group.label}
            </span>
            <span className={`mt-1 block text-2xl font-semibold ${group.tone}`}>{group.value}</span>
          </button>
        ))}
      </section>

      <section className="mb-5 rounded-md border border-border bg-bg-2 p-4">
        <div className="grid gap-5">
          <SegmentedControl
            label="Script result"
            onChange={setFilter}
            options={detectionOptions}
            value={filter}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block font-mono text-xs font-medium uppercase tracking-[0.1em] text-ink-3">
                Search title
              </span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder="Find a KK7 track"
                className="min-h-11 w-full rounded-sm border border-border bg-bg px-3 text-base text-ink outline-none placeholder:text-ink-3 focus:border-accent-line focus:ring-2 focus:ring-accent-ring"
              />
            </label>

            <label className="block">
              <span className="mb-2 block font-mono text-xs font-medium uppercase tracking-[0.1em] text-ink-3">
                Removal status
              </span>
              <select
                value={cohort}
                onChange={(event) =>
                  setCohort(event.currentTarget.value as Kk7BoundaryAuditCohortFilter)
                }
                className="min-h-11 w-full rounded-sm border border-border bg-bg px-3 text-base text-ink outline-none focus:border-accent-line focus:ring-2 focus:ring-accent-ring"
              >
                <option value="all">All · 362</option>
                <option value="removed">Removed · 276</option>
                <option value="retained">Not removed · 86</option>
              </select>
            </label>
          </div>
        </div>
      </section>

      <div className="mb-3 flex items-center justify-between gap-3 font-mono text-xs text-ink-3">
        <span>{visibleTracks.length} shown · violations first, then detector score</span>
        {query || cohort !== 'all' || filter !== 'all' ? <span>Filtered view</span> : null}
      </div>

      {visibleTracks.length === 0 ? (
        <section className="rounded-md border border-border bg-surface p-8 text-center text-sm text-ink-2">
          No KK7 tracks match this view.
        </section>
      ) : (
        <section aria-label="All KK7 opening-boom results">
          <div className="hidden grid-cols-[2.75rem_3.25rem_minmax(12rem,1fr)_8rem_10rem] gap-3 rounded-t-md border border-border bg-bg-2 px-4 py-2 font-mono text-xs uppercase tracking-[0.1em] text-ink-3 md:grid">
            <span aria-hidden="true" />
            <span>Rank</span>
            <span>Track</span>
            <span>Removal</span>
            <span className="text-right">Script result</span>
          </div>
          <div className="grid gap-2 md:block md:overflow-hidden md:rounded-b-md md:border-x md:border-b md:border-border">
            {visibleTracks.map((track) => {
              const item = itemById.get(track.id)!;
              return (
                <AuditTrackRow
                  key={track.id}
                  item={item}
                  onStart={() => startPlayback(track.id)}
                  track={track}
                />
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
